import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SeasonSwitcher from "@/components/app/SeasonSwitcher";
import { NotificationBell, NotificationBanner, useNotifications } from "@/components/app/Notifications";
import {
  LayoutDashboard,
  PenSquare,
  Calendar,
  Users,
  Archive as ArchiveIcon,
  Search as SearchIcon,
  Trophy,
  Crown,
  Home as HomeIcon,
  LogOut,
  LogIn,
  ScanLine,
  ClipboardCheck,
} from "lucide-react";
import { cn } from "@/lib/utils";

const LOGO_URL = "/nphs-logo.png";

const PUBLIC_LINKS = [
  { to: "/", label: "Home", icon: HomeIcon },
  { to: "/schedule", label: "Schedule", icon: Calendar },
  { to: "/search", label: "Players", icon: SearchIcon },
  { to: "/leaders", label: "All-Time", icon: Trophy },
  { to: "/records", label: "Records", icon: Crown },
];
const COACH_LINKS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/stats-entry", label: "Stats Entry", icon: PenSquare },
  { to: "/schedule", label: "Schedule", icon: Calendar },
  { to: "/roster", label: "Roster", icon: Users },
  { to: "/archive", label: "Archive", icon: ArchiveIcon },
  { to: "/practice", label: "Practice", icon: ClipboardCheck },
  { to: "/access-cards", label: "Access Cards", icon: ScanLine },
  { to: "/search", label: "Players", icon: SearchIcon },
  { to: "/leaders", label: "All-Time", icon: Trophy },
  { to: "/records", label: "Records", icon: Crown },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const links = user ? COACH_LINKS : PUBLIC_LINKS;
  const [archiveCount, setArchiveCount] = useState(0);
  const { lastLoginItem, dismissedLoginId, dismissBanner } = useNotifications();
  const bannerItem = lastLoginItem && lastLoginItem.id !== dismissedLoginId ? lastLoginItem : null;

  useEffect(() => {
    if (!user) return;
    api.get("/players", { params: { status_filter: "graduated,inactive" } })
      .then((r) => setArchiveCount(r.data.length))
      .catch(() => {});
  }, [user]);

  return (
    <div className="min-h-screen bg-ink-950 text-white">
      {/* Desktop sidebar */}
      <aside
        data-testid="app-sidebar"
        className="hidden md:flex md:flex-col fixed top-0 left-0 h-screen w-64 bg-ink-900 border-r border-ink-700/60 z-30"
      >
        <div className="px-6 pt-6 pb-4 flex items-center gap-3">
          <img
            src={LOGO_URL}
            alt="NPHS"
            className="w-12 h-12 rounded-full ring-2 ring-burgundy/50 bg-black object-cover"
          />
          <div>
            <p className="font-display text-xl font-black uppercase leading-none tracking-wide">NPHS</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-ink-300 mt-1">Canucks · Boys</p>
          </div>
        </div>

        <nav className="flex-1 px-3 mt-4 space-y-1 overflow-y-auto">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm uppercase tracking-wider font-semibold transition-colors duration-150",
                  isActive
                    ? "bg-burgundy text-white"
                    : "text-ink-300 hover:bg-ink-800 hover:text-white"
                )
              }
            >
              <l.icon className="w-4 h-4" strokeWidth={2.4} />
              <span>{l.label}</span>
              {l.to === "/archive" && archiveCount > 0 && (
                <span className="ml-auto text-[10px] bg-ink-700 px-2 py-0.5 rounded-full">{archiveCount}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <SeasonSwitcher canEdit={!!user} />

        <div className="border-t border-ink-700/60 px-4 py-4">
          {user ? (
            <>
              <p className="eyebrow text-[10px]">Head Coach</p>
              <p className="font-display text-lg font-bold uppercase mt-1 leading-tight">Coach Derrick Canada</p>
              <button
                data-testid="logout-button"
                onClick={async () => { await logout(); nav("/schedule"); }}
                className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-wider text-ink-300 hover:text-white border border-ink-700 rounded-md py-2 hover:bg-ink-800 transition"
              >
                <LogOut className="w-3.5 h-3.5" /> Sign out
              </button>
            </>
          ) : (
            <NavLink
              to="/login"
              data-testid="coach-login-link"
              className="w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-wider text-white bg-burgundy hover:bg-burgundy-hover rounded-md py-2.5 transition"
            >
              <LogIn className="w-3.5 h-3.5" /> Coach Login
            </NavLink>
          )}
        </div>
      </aside>

      <main className="md:ml-64 min-h-screen pb-24 md:pb-0">
        {user && user.role === "coach" && (
          <div className="sticky top-0 z-20 bg-ink-950/95 backdrop-blur border-b border-ink-700/40">
            <NotificationBanner item={bannerItem} onDismiss={() => dismissBanner(bannerItem.id)} />
            <div className="flex justify-end px-4 md:px-8 py-2">
              <NotificationBell />
            </div>
          </div>
        )}
        {user && user.role === "builder" && (
          <div className="sticky top-0 z-20 bg-burgundy/15 border-b border-burgundy/30 backdrop-blur px-4 md:px-8 py-2 text-xs uppercase tracking-wider text-ink-200 flex items-center gap-2" data-testid="builder-mode-banner">
            <span className="inline-block w-2 h-2 rounded-full bg-burgundy" />
            You&apos;re logged in as <span className="text-white font-bold ml-1">{user.name || "Builder"}</span> · Coach can see your activity
          </div>
        )}
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav
        data-testid="mobile-bottom-nav"
        className="md:hidden fixed bottom-0 left-0 right-0 h-16 bg-ink-900 border-t border-ink-700/60 z-30 flex"
      >
        {links.slice(0, 5).map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            className={({ isActive }) =>
              cn(
                "flex-1 flex flex-col items-center justify-center gap-1 text-[10px] uppercase tracking-wider",
                isActive ? "text-white" : "text-ink-300"
              )
            }
          >
            {({ isActive }) => (
              <>
                <l.icon className={cn("w-5 h-5", isActive && "text-burgundy")} strokeWidth={2.4} />
                <span>{l.label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
